import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';
import { generatePageSEO } from '../config/seo';

const Privacy = () => {
  const seoData = generatePageSEO('privacy', {
    path: '/privacy',
  });

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <SEOHead
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords.join(', ')}
        canonical={seoData.canonical}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Privacy Policy',
          description: seoData.description,
          url: seoData.canonical,
        }}
      />
      {/* Navigation */}
      <nav className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">M</span>
              </div>
              <span className="text-xl font-bold text-gradient">Mahstry Private Limited</span>
            </Link>
            
            <div className="flex items-center space-x-8">
              <Link to="/" className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400">
                Home
              </Link>
              <Link to="/login" className="btn-primary">
                Login
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card p-8"
          >
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
              Mahstry Private Limited – Privacy Policy
            </h1>
            
            <div className="prose prose-lg dark:prose-invert max-w-none">
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                <strong>Last Updated:</strong> January 2025<br />
                <strong>Effective From:</strong> January 2025
              </p>
              
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                This Privacy Policy is provided by Mahstry Private Limited (GSTIN: 08AAUCM5530E1Z9), the legal entity operating the MahStry platform. We respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, store, and protect your data.
              </p>
              
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                1. Information We Collect
              </h2>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                1.1 Personal Information
              </h3>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                <li>Name</li>
                <li>Email</li>
                <li>Phone number</li>
                <li>Location</li>
                <li>Class, subject, exam preferences</li>
                <li>Date of birth (if provided)</li>
                <li>Account credentials</li>
                <li>Teacher documents (for verification)</li>
                <li>Institution details (for onboarding)</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                1.2 Usage Information
              </h3>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                <li>Login history</li>
                <li>Study streak</li>
                <li>To-do list usage</li>
                <li>Motivation widget interactions</li>
                <li>Classes attended</li>
                <li>Courses enrolled</li>
                <li>Mock test attempts</li>
                <li>Wrong/skipped questions</li>
                <li>Bookmarks</li>
                <li>Resources viewed</li>
                <li>Doubts asked</li>
                <li>Dashboard navigation</li>
                <li>Notifications opened</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                1.3 Device Information
              </h3>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                <li>IP address</li>
                <li>Device type (mobile/desktop)</li>
                <li>Browser</li>
                <li>OS</li>
                <li>Cookies</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                1.4 Payments Information
              </h3>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                <li>Transaction IDs</li>
                <li>Payment confirmations</li>
                <li>Order history</li>
              </ul>
              <p className="text-gray-600 dark:text-gray-400 mb-6 italic">
                (Note: We do NOT store card/bank details.)
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                1.5 Test Proctoring Data
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-3">
                During mock tests, we may collect proctoring data to prevent cheating and maintain test integrity:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-4 space-y-2">
                <li>Screen activity recordings (if enabled)</li>
                <li>Video/audio capture (if enabled)</li>
                <li>Browser activity logs</li>
                <li>Tab switching events</li>
                <li>Application focus detection</li>
              </ul>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                <strong>Critical Privacy Protections:</strong> This proctoring data is collected ONLY for the purpose of preventing cheating during the test session. It is NOT used for analytics, behavioral tracking, profiling, or any other purposes. See Section 2.6 for detailed usage restrictions.
              </p>
              
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                2. How We Use Your Information
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                We use your data to:
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                2.1 Provide Core Services
              </h3>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                <li>Enroll you in batches/courses</li>
                <li>Provide personalized content</li>
                <li>Deliver live classes</li>
                <li>Generate analytics and reports</li>
                <li>Track your progress and test results</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                2.2 Improve User Experience
              </h3>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                <li>Study streak tracking</li>
                <li>Revision suggestions</li>
                <li>Motivational content</li>
                <li>To-do list features</li>
                <li>Performance comparison</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                2.3 Teacher & Institution Services
              </h3>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                <li>Verification</li>
                <li>Dashboard access</li>
                <li>Content management</li>
                <li>Student tracking</li>
                <li>Exam/test creation tools</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                2.4 Security & Fraud Prevention
              </h3>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                <li>Detect suspicious behavior</li>
                <li>Prevent content piracy</li>
                <li>Protect copyrighted material</li>
                <li>Block unauthorized downloads</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                2.5 Test Proctoring & Integrity
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-3">
                Proctoring data is used EXCLUSIVELY for:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-4 space-y-2">
                <li>Detecting cheating attempts during test sessions</li>
                <li>Verifying test integrity</li>
                <li>Investigating specific violations when reported</li>
              </ul>
              <p className="text-gray-600 dark:text-gray-400 mb-3">
                <strong>Strict Usage Restrictions:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2 ml-4">
                <li><strong>NO Analytics:</strong> Proctoring data is NEVER used for analytics, performance metrics, or any form of data analysis beyond cheating detection.</li>
                <li><strong>NO Behavioral Tracking:</strong> Proctoring data is NEVER used to track user behavior, create profiles, or analyze learning patterns.</li>
                <li><strong>NO Sharing:</strong> Proctoring data is NEVER shared with third parties, advertisers, marketing companies, or any external entities (except as required by law or for investigating specific violations).</li>
                <li><strong>NO Secondary Use:</strong> Proctoring data is NEVER used for any purpose other than test integrity verification during the examination period.</li>
                <li><strong>Limited Retention:</strong> Proctoring data is automatically deleted within 30 days after test completion, unless required for investigation of specific cheating violations.</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                2.6 Communication
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                We may send:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2 ml-4">
                <li>Important account updates</li>
                <li>Test notifications</li>
                <li>Batch reminders</li>
                <li>Payment confirmations</li>
                <li>Security alerts</li>
              </ul>
              
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                3. Data Privacy & Protection
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                <strong>Our Commitment to Your Privacy:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-4 space-y-2 ml-4">
                <li><strong>We DO NOT sell your personal data to anyone.</strong></li>
                <li><strong>We DO NOT use your data for advertising or marketing purposes without your explicit consent.</strong></li>
                <li><strong>We DO NOT share your data with third parties except as explicitly stated in this policy.</strong></li>
                <li><strong>We DO NOT use proctoring data for analytics, behavioral tracking, or any purpose beyond test integrity.</strong></li>
                <li><strong>You have the right to access, modify, or delete your data at any time.</strong></li>
                <li><strong>We implement industry-standard security measures to protect your data.</strong></li>
              </ul>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                For detailed information about your rights and how to exercise them, see Section 5 (Your Rights).
              </p>
              
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                4. Sharing of Information
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                <strong>We DO NOT sell your data.</strong>
              </p>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                We may share information only with:
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                4.1 Teachers & Institutions
              </h3>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-4 space-y-2">
                <li>Your name and enrollment status</li>
                <li>Test performance (only for batches you joined)</li>
                <li>Doubts asked</li>
                <li>Attendance</li>
              </ul>
              <p className="text-gray-600 dark:text-gray-400 mb-6 italic">
                (They will not see your personal phone or email unless you choose to share.)
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                4.2 Service Providers
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                For purposes such as:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-4 space-y-2 ml-4">
                <li>Payment processing</li>
                <li>Hosting</li>
                <li>Security</li>
                <li>Analytics</li>
              </ul>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Providers comply with strict confidentiality obligations.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                4.3 Legal Authorities
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                <strong>Important Note on Proctoring Data:</strong> Proctoring data is NEVER shared with third parties, service providers, or any external entities except when:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2 ml-4">
                <li>Required by a valid court order or legal subpoena</li>
                <li>Necessary for investigating a specific cheating violation reported by an institution or teacher</li>
              </ul>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                If required by:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2 ml-4">
                <li>Court order</li>
                <li>Law enforcement</li>
                <li>Legal investigation</li>
              </ul>
              
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                5. Data Storage & Security
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                We use:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-4 space-y-2 ml-4">
                <li>Encrypted databases</li>
                <li>Firewalls</li>
                <li>Secure authentication</li>
                <li>Access control systems</li>
                <li>Regular audits</li>
              </ul>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Despite best efforts, no system is 100% secure.
              </p>
              
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                6. Your Rights
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                You may:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-4 space-y-2 ml-4">
                <li>Access your data</li>
                <li>Request correction</li>
                <li>Delete your account</li>
                <li>Change exam preferences</li>
                <li>Modify privacy settings</li>
                <li>Disable notifications</li>
              </ul>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Account deletion removes:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-4 space-y-2 ml-4">
                <li>Personal information</li>
                <li>Test history</li>
                <li>Batches/courses enrolled</li>
                <li>Bookmarks</li>
                <li>Doubts asked</li>
              </ul>
              <p className="text-gray-600 dark:text-gray-400 mb-6 italic">
                (Some anonymized analytics may be retained for system improvement.)
              </p>
              
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                7. Children's Privacy
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                The MahStry platform is not intended for children under 13.<br />
                We delete any data accidentally collected from minors.
              </p>
              
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                8. Cookies
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                We use cookies for:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2 ml-4">
                <li>Login sessions</li>
                <li>Performance measurement</li>
                <li>Personalization</li>
                <li>Preventing misuse</li>
              </ul>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                You may disable cookies but some features will not work.
              </p>
              
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                9. Data Retention
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                <strong>General Data Retention:</strong> We retain user data until:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-4 space-y-2 ml-4">
                <li>Your account is deleted, OR</li>
                <li>Retention is legally required.</li>
              </ul>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Teacher/institution documents are deleted upon profile deletion.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                9.1 Proctoring Data Retention
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                <strong>Strict Retention Policy for Proctoring Data:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-4 space-y-2 ml-4">
                <li><strong>Automatic Deletion:</strong> All proctoring data (screen recordings, video/audio captures, activity logs) is automatically deleted within 30 days after test completion.</li>
                <li><strong>No Long-Term Storage:</strong> Proctoring data is NOT stored indefinitely. It is purged from our systems after the retention period.</li>
                <li><strong>Exception:</strong> Proctoring data may be retained longer ONLY if there is an active investigation into a specific cheating violation. Once the investigation is complete, the data is deleted within 7 days.</li>
                <li><strong>No Backup Retention:</strong> Proctoring data is NOT included in long-term backups. Once deleted, it cannot be recovered.</li>
              </ul>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                This ensures that your privacy is protected and proctoring data does not accumulate over time.
              </p>
              
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                10. Changes to This Policy
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                We may update this Privacy Policy anytime.<br />
                Continued use means you accept the updated policy.
              </p>
              
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                11. Contact
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                For privacy concerns, write to:<br />
                📩 <a href="mailto:support@mahstry.com" className="text-primary-600 hover:text-primary-700">support@mahstry.com</a>
              </p>
              
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <strong>Legal Entity:</strong> Mahstry Private Limited<br />
                  <strong>GSTIN:</strong> 08AAUCM5530E1Z9
                </p>
              </div>
            </div>
            
            <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
              <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
                ← Back to Home
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
